import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  List,
  Snackbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import TreeNode from './TreeNode';
import NodeDetailDrawer from './NodeDetailDrawer';
import PublishIcon from '@mui/icons-material/Publish';
import {
  TutorStructureNode,
  addNode,
  deleteNode,
  moveNode,
  publishVersion,
  updateNode,
} from '../../../services/tutorStructureService';

interface CourseTreePanelProps {
  courseId: number;
  nodes: TutorStructureNode[];
  onNodesChange: (nodes: TutorStructureNode[]) => void;
  onVersionPublished?: () => void;
  onContentEdit?: (nodeId: number) => void;
}

function RootDropZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'root-drop-zone' });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        minHeight: 120,
        border: '2px dashed',
        borderColor: isOver ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 1,
        transition: 'border-color 0.15s',
      }}
    >
      {children}
    </Box>
  );
}

export default function CourseTreePanel({ courseId, nodes, onNodesChange, onVersionPublished, onContentEdit }: CourseTreePanelProps) {
  const [selectedNode, setSelectedNode] = useState<TutorStructureNode | null>(null);
  const [activeNode, setActiveNode] = useState<TutorStructureNode | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<'error' | 'success'>('error');
  const [publishing, setPublishing] = useState(false);

  const handlePublishVersion = async () => {
    setPublishing(true);
    try {
      const version = await publishVersion(courseId);
      setToastSeverity('success');
      setToast(`Version ${version.versionLabel} published`);
      onVersionPublished?.();
    } catch (err: any) {
      setToastSeverity('error');
      if (err?.response?.status === 400) {
        setToast('Cannot publish: course structure is empty.');
      } else {
        setToast('Failed to publish version. Please try again.');
      }
    } finally {
      setPublishing(false);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const flatIds = nodes.map((n) => n.id);

  const handleAddCustomTopic = async () => {
    try {
      const created = await addNode(courseId, { nodeType: 'CUSTOM_TOPIC', label: 'New Topic' });
      onNodesChange([...nodes, created]);
    } catch {
      setToast('Failed to add topic. Please try again.');
    }
  };

  const handleAddWrapper = async () => {
    try {
      const created = await addNode(courseId, { nodeType: 'WRAPPER', label: 'New Section' });
      onNodesChange([...nodes, created]);
    } catch {
      setToast('Failed to add section. Please try again.');
    }
  };

  const handleDelete = useCallback(
    async (node: TutorStructureNode) => {
      const hasChildren = node.children && node.children.length > 0;
      try {
        await deleteNode(courseId, node.id, hasChildren ? true : undefined);
        onNodesChange(removeNode(nodes, node.id));
      } catch (err: any) {
        if (err?.response?.status === 422) {
          // Prompt handled in NodeDetailDrawer; re-attempt with cascade confirm
          await deleteNode(courseId, node.id, true);
          onNodesChange(removeNode(nodes, node.id));
        } else {
          setToast('Failed to delete node. Please try again.');
        }
      }
    },
    [courseId, nodes, onNodesChange]
  );

  const handleLabelSave = async (nodeId: number, label: string) => {
    const updated = await updateNode(courseId, nodeId, { label });
    onNodesChange(patchNode(nodes, nodeId, (n) => ({ ...n, label: updated.label })));
  };

  const handleDescriptionSave = async (nodeId: number, description: string) => {
    const updated = await updateNode(courseId, nodeId, { description });
    onNodesChange(patchNode(nodes, nodeId, (n) => ({ ...n, description: updated.description })));
    if (selectedNode?.id === nodeId) {
      setSelectedNode((prev) => prev ? { ...prev, description: updated.description } : prev);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type === 'TREE_NODE') {
      setActiveNode(data.node as TutorStructureNode);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveNode(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const MAX_DEPTH = 3;

    // Library topic dropped into tree
    if (activeData?.type === 'LIBRARY_TOPIC') {
      const targetParentId = overData?.type === 'TREE_NODE' ? (overData.node as TutorStructureNode).id : null;
      if (targetParentId !== null) {
        const parentDepth = getNodeDepth(nodes, targetParentId);
        if (parentDepth >= MAX_DEPTH) {
          setToast('Maximum nesting depth reached');
          return;
        }
      }
      try {
        const created = await addNode(courseId, {
          nodeType: 'TOPIC_REF',
          refId: activeData.topicId,
          label: activeData.title,
          parentId: targetParentId,
        });
        onNodesChange([...nodes, created]);
      } catch {
        setToast('Failed to add topic from library. Please try again.');
      }
      return;
    }

    // Tree node reorder / reparent
    if (activeData?.type === 'TREE_NODE') {
      const draggedId = Number(active.id);
      const newParentId = overData?.type === 'TREE_NODE' && over.id !== 'root-drop-zone' ? Number(over.id) : null;
      if (newParentId !== null) {
        const parentDepth = getNodeDepth(nodes, newParentId);
        if (parentDepth >= MAX_DEPTH) {
          setToast('Maximum nesting depth reached');
          return;
        }
      }
      const newIndex = flatIds.indexOf(Number(over.id));
      try {
        await moveNode(courseId, draggedId, {
          newParentId,
          newIndex: newIndex >= 0 ? newIndex : 0,
        });
        // Refresh from server not needed for MVP — optimistic reorder via page-level refresh
      } catch {
        setToast('Failed to reorder. Please try again.');
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Course Structure
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddWrapper} variant="outlined">
            Add section
          </Button>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddCustomTopic} variant="outlined">
            Add custom topic
          </Button>
          <Button
            size="small"
            startIcon={<PublishIcon />}
            onClick={handlePublishVersion}
            variant="contained"
            color="primary"
            disabled={publishing}
          >
            {publishing ? 'Publishing…' : 'Publish version'}
          </Button>
        </Box>
      </Box>
      <Divider />

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext items={flatIds} strategy={verticalListSortingStrategy}>
          <RootDropZone>
            {nodes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 3 }}>
                Drag topics from the library or use the buttons above to build your course structure.
              </Typography>
            ) : (
              <List dense disablePadding>
                {nodes.map((node) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    depth={0}
                    onSelect={setSelectedNode}
                    onDelete={handleDelete}
                    onContentEdit={onContentEdit}
                  />
                ))}
              </List>
            )}
          </RootDropZone>
        </SortableContext>

        <DragOverlay>
          {activeNode && (
            <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'primary.main', borderRadius: 1, px: 2, py: 0.5 }}>
              <Typography variant="body2">{activeNode.label}</Typography>
            </Box>
          )}
        </DragOverlay>
      </DndContext>

      <NodeDetailDrawer
        node={selectedNode}
        courseId={courseId}
        onClose={() => setSelectedNode(null)}
        onLabelSave={handleLabelSave}
        onDescriptionSave={handleDescriptionSave}
        onDelete={handleDelete}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toastSeverity} onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

function removeNode(nodes: TutorStructureNode[], id: number): TutorStructureNode[] {
  return nodes
    .filter((n) => n.id !== id)
    .map((n) => ({ ...n, children: removeNode(n.children, id) }));
}

function patchNode(
  nodes: TutorStructureNode[],
  id: number,
  updater: (n: TutorStructureNode) => TutorStructureNode
): TutorStructureNode[] {
  return nodes.map((n) =>
    n.id === id ? updater(n) : { ...n, children: patchNode(n.children, id, updater) }
  );
}

function getNodeDepth(nodes: TutorStructureNode[], targetId: number, currentDepth = 0): number {
  for (const node of nodes) {
    if (node.id === targetId) return currentDepth;
    const found = getNodeDepth(node.children, targetId, currentDepth + 1);
    if (found >= 0) return found;
  }
  return -1;
}
